import { useEffect, useRef, useState } from "react";
import { acquireSocket, releaseSocket } from "../lib/socket";
import type { GameTimePayload } from "../types/api";

type Status = "lobby" | "active" | "ended";

type TimeLike = {
  code?: string;
  status?: Status | string;
  duration_seconds?: number;
  started_at?: string | null;
  ends_at?: string | null;
  server_now?: string;
  remaining_ms?: number | null;
};

function buildApiUrl(path: string) {
  const API = import.meta.env.VITE_API_BASE ?? "/api";
  const base = /^https?:\/\//i.test(API)
    ? new URL(API)
    : new URL(API, window.location.origin);
  const basePath = base.pathname.endsWith("/")
    ? base.pathname.slice(0, -1)
    : base.pathname;
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${base.origin}${basePath}${suffix}`;
}

/**
 * Game clock. The end time is fetched once and then kept current by server
 * pushes: start, extend and end all arrive over the socket, so nothing here
 * polls. The interval below only advances the local display.
 */
export function useGlobalCountdown(gameCode: string) {
  const [status, setStatus] = useState<Status>("lobby");
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  const statusRef = useRef<Status>("lobby");
  const offsetRef = useRef(0); // server_now - client_now
  const endsAtRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);

  /* The label only shows mm:ss, so pushing a new value four times a second
     re-rendered the whole page: every challenge card included: to redraw
     the same two digits. Only publish when the displayed second changes. */
  const lastSecondRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const tick = () => {
      if (!endsAtRef.current) {
        if (lastSecondRef.current !== null) {
          lastSecondRef.current = null;
          setRemainingMs(null);
        }
        return;
      }
      const now = Date.now() + offsetRef.current;
      const rem = Math.max(0, endsAtRef.current - now);

      const second = Math.floor(rem / 1000);
      if (second !== lastSecondRef.current) {
        lastSecondRef.current = second;
        setRemainingMs(rem);
      }

      const next: Status = rem <= 0 ? "ended" : "active";
      if (next !== statusRef.current) {
        statusRef.current = next;
        setStatus(next);
      }
    };

    /* Handlers live out here so the cleanup can detach them. They used to be
       registered inside the async bootstrap with no matching `off`, which
       leaked a new set onto the shared socket on every remount. */
    const socket = acquireSocket();

    const rejoin = () => socket.emit("game:join", gameCode);

    const onStarted = (msg: GameTimePayload) => {
      if (msg?.code !== gameCode) return;
      if (msg.server_now) {
        offsetRef.current = new Date(msg.server_now).getTime() - Date.now();
      }
      endsAtRef.current = msg.ends_at ? new Date(msg.ends_at).getTime() : null;
      statusRef.current = "active";
      setStatus("active");
      tick();
    };

    const onTimeAdjusted = (msg: GameTimePayload) => {
      if (msg?.code !== gameCode) return;
      endsAtRef.current = msg.ends_at ? new Date(msg.ends_at).getTime() : null;
      tick();
    };

    const onEnded = (msg: GameTimePayload) => {
      if (msg?.code !== gameCode) return;
      endsAtRef.current = msg.ends_at ? new Date(msg.ends_at).getTime() : null;
      statusRef.current = "ended";
      setStatus("ended");
      setRemainingMs(0);
    };

    socket.on("connect", rejoin);
    socket.on("reconnect", rejoin);
    socket.on("game:started", onStarted);
    socket.on("game:time_adjusted", onTimeAdjusted);
    socket.on("game:ended", onEnded);
    if (socket.connected) rejoin();

    async function bootstrap() {
      // 1) Try /time
      let data: TimeLike | null = null;
      try {
        const r = await fetch(
          buildApiUrl(`/public/games/${encodeURIComponent(gameCode)}/time`),
          { headers: { Accept: "application/json" } }
        );
        if (r.ok) {
          const j = (await r.json().catch(() => null)) as TimeLike | null;
          if (j && (j.server_now || j.ends_at)) data = j;
        }
      } catch {
        /* Bootstrap is best effort; the socket still drives updates. */
      }

      // 2) Fallback /status (at least set a textual status)
      if (!data) {
        try {
          const r = await fetch(
            buildApiUrl(`/public/games/${encodeURIComponent(gameCode)}/status`),
            { headers: { Accept: "application/json" } }
          );
          if (r.ok) {
            const sdata: { status?: string } = await r.json().catch(() => ({}));
            const st =
              typeof sdata?.status === "string"
                ? (sdata.status.toLowerCase() as Status)
                : undefined;
            data = { status: st, server_now: new Date().toISOString() };
          }
        } catch {
        /* Bootstrap is best effort; the socket still drives updates. */
      }
      }

      if (cancelled || !data) return;

      if (typeof data.status === "string") {
        const norm = data.status.toLowerCase() as Status;
        statusRef.current = norm;
        setStatus(norm);
      }

      offsetRef.current = data.server_now
        ? new Date(data.server_now).getTime() - Date.now()
        : 0;

      endsAtRef.current = data.ends_at
        ? new Date(data.ends_at).getTime()
        : null;

      tick();
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      intervalRef.current = window.setInterval(tick, 250);
    }

    bootstrap();

    return () => {
      cancelled = true;
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      socket.off("connect", rejoin);
      socket.off("reconnect", rejoin);
      socket.off("game:started", onStarted);
      socket.off("game:time_adjusted", onTimeAdjusted);
      socket.off("game:ended", onEnded);
      releaseSocket();
    };
  }, [gameCode]);

  const label = (ms = remainingMs) => {
    if (ms == null) return "--:--";
    const s = Math.max(0, Math.floor(ms / 1000));
    const mm = Math.floor(s / 60)
      .toString()
      .padStart(2, "0");
    const ss = (s % 60).toString().padStart(2, "0");
    return `${mm}:${ss}`;
  };

  return { status, remainingMs, label };
}
